import React from "react";
import {
  Loader,
  LoaderSizes,
  Pagination,
  SearchInput,
  Select,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppSelector } from "../../../../../../redux";
import moment from "moment";
import "./HistoryLog.scss";

const HistoryLog = () => {
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(20);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [typeFilter, setTypeFilter] = React.useState<string>("All Types");
  const { partnerLogs, status } = useAppSelector((state) => state.partner_logs);

  React.useEffect(() => {
    setCurrentPageNumber(1);
  }, [searchTerm, typeFilter]);

  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber);
  };

  const onPageSizesClick = (e: any, value: any) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("D MMMM YYYY");
  };

  const toTitleCase = (string: string): string => {
    return string
      .toLowerCase()
      .split("_")
      .map(function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  };

  // Get formatted types for filter dropdown
  const filterOptions = React.useMemo(() => {
    return [
      { label: "All Types", value: "All Types" },
      ...Array.from(new Set(partnerLogs.map((log) => log.type))).map(
        (type) => ({
          label: toTitleCase(type),
          value: type,
        })
      ),
    ];
  }, [partnerLogs]);

  const filteredPartnerLogs = partnerLogs.filter((log) => {
    const matchesSearch =
      searchTerm.length === 0 ||
      log.message.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      typeFilter === "All Types" ||
      log.type.toLowerCase() === typeFilter.toLowerCase();

    return matchesSearch && matchesType;
  });

  const totalRecords = filteredPartnerLogs.length;

  const paginatedData = filteredPartnerLogs.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  return (
    <div className="history-log-container">
      <div className="search-filter-container">
        <div className="search-input-wrapper">
          <SearchInput
            theme="dark"
            id="searchLogs"
            name="searchLogs"
            placeholder="Search logs by message"
            isDisplayClearIcon={true}
            value={searchTerm}
            onChange={(event: any) => setSearchTerm(event.currentTarget.value)}
            onSearch={(event: any, value: string) => {
              setSearchTerm(value);
            }}
          />
        </div>
        <div className="filter-select-wrapper">
          <Select
            id="typeFilter"
            name="typeFilter"
            placeholder="Select type"
            options={filterOptions}
            value={typeFilter}
            onChange={(event: any) => setTypeFilter(event.currentTarget.value)}
          />
        </div>
      </div>

      {status.partnerLogsLoading ? (
        <div className="list-loading">
          <Loader variant={LoaderSizes.large} />
        </div>
      ) : (
        <>
          <div className="table-container">
            <div className="table-header">
              <div className="col-1">Timestamp</div>
              <div className="col-2">Type</div>
              <div className="col-3">Message</div>
              <div className="col-4">Status</div>
              <div className="col-5">Initiated By</div>
            </div>

            {totalRecords > 0 ? (
              paginatedData.map((log, index) => {
                return (
                  <div
                    className="table-row"
                    key={log.ID}
                    style={index === 0 ? { border: "none" } : {}}
                  >
                    <div className="col-1">
                      <span>{formatDate(log.created_at)}</span>
                    </div>
                    <div className="col-2">
                      <span className="type-badge">
                        {toTitleCase(log.type)}
                      </span>
                    </div>
                    <div className="col-3">{log.message}</div>
                    <div className="col-4">
                      <span
                        className={`status-badge ${
                          log.status === "true"
                            ? "success"
                            : log.status === "false"
                            ? "error"
                            : "neutral"
                        }`}
                      >
                        {log.status === "true"
                          ? "SUCCESS"
                          : log.status === "false"
                          ? "ERROR"
                          : "N/A"}
                      </span>
                    </div>
                    <div className="col-5">
                      {log.initiatedBy || "System generated log"}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-data">No data available for display.</div>
            )}
          </div>
          <Pagination
            currentPageNumber={currentPageNumber}
            totalRecords={totalRecords}
            pageSize={pageSize}
            border={true}
            onPageNumberClick={handlePageChange}
            onPageSizesClick={onPageSizesClick}
            pageSizes={[
              { label: "20", value: "20" },
              { label: "50", value: "50" },
              { label: "100", value: "100" },
            ]}
          />
        </>
      )}
    </div>
  );
};

export default HistoryLog;
